import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Input,
  Layout,
  Pagination,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { useTableSkeleton } from '@/components/skeletons/useTableSkeleton';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import { canBanUser, getAssignableRoles, Roles } from '@/core/utils/roles';
import { Order_By } from '@/generated/graphql/graphql';
import type { EditUserPermissions, EditUserSubmitData } from './components/EditUserModalView';
import { EditUserModalView } from './components/EditUserModalView';
import { NewUserModalView } from './components/NewUserModalView';
import {
  useDeleteUserDepartments,
  useInsertUserDepartments,
} from './query/users.departments.hooks';
import type { ExtendedUser } from './query/users.hooks';
import { useBlockUser, useEditUser, useGetUsers } from './query/users.hooks';

const { Search } = Input;

const MAX_VISIBLE_TAGS = 3;

type UserRecord = ExtendedUser;

export const UserEditPage: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { userDataParams } = useAuth();

  const currentUserRoles = useMemo(() => userDataParams?.roles || [], [userDataParams?.roles]);

  const isSeniorManager = useMemo(
    () => currentUserRoles.includes(Roles.seniorManager),
    [currentUserRoles]
  );

  const isManager = useMemo(() => currentUserRoles.includes(Roles.manager), [currentUserRoles]);

  const canEditName = useMemo(() => {
    return (
      currentUserRoles.includes(Roles.manager) ||
      currentUserRoles.includes(Roles.seniorOperator) ||
      currentUserRoles.includes(Roles.seniorManager)
    );
  }, [currentUserRoles]);

  const canEditDepartments = isSeniorManager;

  const canEditRoles = useCallback(
    (targetUserRoles: string[]) => {
      if (isSeniorManager) {
        return true;
      }

      if (isManager) {
        const hasHigherRole =
          targetUserRoles.includes(Roles.manager) || targetUserRoles.includes(Roles.seniorManager);
        return !hasHigherRole;
      }

      return false;
    },
    [isSeniorManager, isManager]
  );

  const assignableRoles = useMemo(() => getAssignableRoles(currentUserRoles), [currentUserRoles]);

  const canEditUser = useCallback(
    (targetUserRoles: string[]) => {
      if (!canEditName) return false;

      if (currentUserRoles.includes(Roles.seniorManager)) {
        return true;
      } else if (currentUserRoles.includes(Roles.seniorOperator)) {
        return true;
      } else if (currentUserRoles.includes(Roles.manager)) {
        return !targetUserRoles.includes(Roles.manager);
      }

      return false;
    },
    [canEditName, currentUserRoles]
  );

  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sortOrder, setSortOrder] = useState<{ [key: string]: Order_By }>({});
  const { selectedDepartmentId: departmentId } = useDepartments();
  const blockUserMutation = useBlockUser();
  const editUser = useEditUser();
  const deleteUserDepartments = useDeleteUserDepartments();
  const insertUserDepartments = useInsertUserDepartments();

  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);

  const {
    data: usersData,
    isLoading: usersLoading,
    refetch,
  } = useGetUsers({
    limit: pagination.pageSize,
    offset: (pagination.current - 1) * pagination.pageSize,
    where: {
      blocked: { _eq: switchChecked },
      departments_users: { departmentId: { _eq: departmentId } },
      _or: [{ name: { _ilike: `%${searchText}%` } }, { email: { _ilike: `%${searchText}%` } }],
    },
    sorters: sortOrder,
  });

  const editingUserPermissions = useMemo<EditUserPermissions>(() => {
    if (!editingUser) {
      return {
        canEditNameAndTelegram: false,
        canEditDepartments: false,
        canEditRoles: false,
        canBan: false,
      };
    }
    const targetRoles = (editingUser.roles as string[]) || [];
    return {
      canEditNameAndTelegram: canEditUser(targetRoles),
      canEditDepartments,
      canEditRoles: canEditRoles(targetRoles) && assignableRoles.length > 0,
      canBan: canBanUser(currentUserRoles, targetRoles),
    };
  }, [
    editingUser,
    canEditUser,
    canEditDepartments,
    canEditRoles,
    assignableRoles,
    currentUserRoles,
  ]);

  const editLoading =
    editUser.isPending || deleteUserDepartments.isPending || insertUserDepartments.isPending;

  const buildUserUpdateTask = useCallback(
    (user: UserRecord, data: EditUserSubmitData): Promise<unknown> | null => {
      const targetRoles = (user.roles as string[]) || [];
      const userUpdate: { name?: string; telegramName?: string; roles?: string[] } = {};

      if (canEditUser(targetRoles)) {
        if (data.name !== (user.name || '')) {
          userUpdate.name = data.name;
        }
        if (data.telegramName !== (user.telegramName || '')) {
          userUpdate.telegramName = data.telegramName;
        }
      }

      if (canEditRoles(targetRoles) && assignableRoles.length > 0) {
        const oldRoles = [...((user.roles as string[]) || [])].sort();
        const newRoles = [...data.roles].sort();
        if (JSON.stringify(oldRoles) !== JSON.stringify(newRoles)) {
          userUpdate.roles = data.roles;
        }
      }

      if (Object.keys(userUpdate).length === 0) return null;

      return editUser.mutateAsync({ id: user.id, user: userUpdate });
    },
    [canEditUser, canEditRoles, assignableRoles, editUser]
  );

  const buildDepartmentUpdateTask = useCallback(
    (user: UserRecord, departmentIds: number[]): Promise<unknown> | null => {
      if (!canEditDepartments) return null;

      const oldDeptIds = (user.departments_users || [])
        .filter((d) => d.department !== null)
        .map((d) => d.departmentId)
        .sort((a, b) => a - b);
      const newDeptIds = [...departmentIds].sort((a, b) => a - b);

      if (JSON.stringify(oldDeptIds) === JSON.stringify(newDeptIds)) return null;

      return (async () => {
        await deleteUserDepartments.mutateAsync({ userId: user.id });
        if (departmentIds.length > 0) {
          await insertUserDepartments.mutateAsync({
            objects: departmentIds.map((departmentId) => ({
              userId: user.id,
              departmentId,
            })),
          });
        }
      })();
    },
    [canEditDepartments, deleteUserDepartments, insertUserDepartments]
  );

  const handleEditSubmit = useCallback(
    async (data: EditUserSubmitData) => {
      if (!editingUser) return;

      try {
        const tasks: Promise<unknown>[] = [];
        const userTask = buildUserUpdateTask(editingUser, data);
        if (userTask) tasks.push(userTask);
        const deptTask = buildDepartmentUpdateTask(editingUser, data.departmentIds);
        if (deptTask) tasks.push(deptTask);

        if (tasks.length === 0) {
          setEditingUser(null);
          return;
        }

        await Promise.all(tasks);
        setEditingUser(null);
        await refetch();
        messageApi.success('User updated');
      } catch (err) {
        const error = err as Error;
        messageApi.error(`Failed to update user: ${error?.message || error}`);
      }
    },
    [editingUser, buildUserUpdateTask, buildDepartmentUpdateTask, refetch, messageApi]
  );

  const handleBanFromModal = useCallback(async () => {
    if (!editingUser) return;
    try {
      await blockUserMutation.mutateAsync({ userId: editingUser.id });
      messageApi.success('Success!');
      setEditingUser(null);
    } catch (error) {
      messageApi.error(`Failed to update user status: ${error}`);
    }
  }, [editingUser, blockUserMutation, messageApi]);

  const onSearch = useCallback((value: string) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const handleAddUser = useCallback(() => setModalVisible(true), []);

  const handleTableChange = useCallback(
    (
      _pagination: unknown,
      _filters: unknown,
      sorter:
        | {
            field?: string | number | bigint | readonly (string | number | bigint)[];
            order?: 'ascend' | 'descend' | null;
          }
        | {
            field?: string | number | bigint | readonly (string | number | bigint)[];
            order?: 'ascend' | 'descend' | null;
          }[]
    ) => {
      if (!sorter || Array.isArray(sorter)) {
        setSortOrder({});
        return;
      }
      if (sorter.field !== 'name' || !sorter.order) {
        setSortOrder({});
        return;
      }
      setSortOrder({ name: sorter.order === 'ascend' ? Order_By.Asc : Order_By.Desc });
    },
    []
  );

  const columns = useMemo(
    () => [
      { dataIndex: 'email', key: 'email', title: 'Email' },
      {
        dataIndex: 'telegramName',
        key: 'telegram',
        title: 'Telegram',
        render: (telegram: string) => <Typography.Text>{telegram || '-'}</Typography.Text>,
      },
      {
        dataIndex: 'name',
        key: 'name',
        title: 'Name',
        sorter: true,
        sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      },
      {
        dataIndex: 'departments_users',
        key: 'departments',
        title: 'Departments',
        render: (departments: UserRecord['departments_users']) => {
          const validDepartments = (departments || []).filter(
            (
              d
            ): d is UserRecord['departments_users'][number] & {
              department: NonNullable<UserRecord['departments_users'][number]['department']>;
            } => d.department !== null
          );
          if (validDepartments.length === 0) {
            return <Typography.Text type="secondary">-</Typography.Text>;
          }
          const sorted = [...validDepartments].sort((a, b) =>
            a.department.name.localeCompare(b.department.name)
          );
          const visible = sorted.slice(0, MAX_VISIBLE_TAGS);
          const hidden = sorted.slice(MAX_VISIBLE_TAGS);
          return (
            <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
              {visible.map((d) => (
                <Tag color="green" key={d.id}>
                  {d.department.name}
                </Tag>
              ))}
              {hidden.length > 0 && (
                <Tooltip title={hidden.map((d) => d.department.name).join(', ')}>
                  <Tag style={{ cursor: 'pointer' }}>+{hidden.length} more</Tag>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        dataIndex: 'roles',
        key: 'roles',
        title: 'Roles',
        render: (roles: string[]) => {
          const sorted = [...(roles || [])].sort((a, b) => a.localeCompare(b));
          const visible = sorted.slice(0, MAX_VISIBLE_TAGS);
          const hidden = sorted.slice(MAX_VISIBLE_TAGS);
          return (
            <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
              {visible.map((role) => (
                <Tag color="blue" key={role}>
                  {role}
                </Tag>
              ))}
              {hidden.length > 0 && (
                <Tooltip title={hidden.join(', ')}>
                  <Tag style={{ cursor: 'pointer' }}>+{hidden.length} more</Tag>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        dataIndex: 'firebaseMessagingToken',
        key: 'token',
        title: 'Token',
        render: (token: string) =>
          token ? (
            <CheckOutlined style={{ color: 'green' }} />
          ) : (
            <CloseOutlined style={{ color: 'red' }} />
          ),
      },
      {
        dataIndex: 'login_logs',
        key: 'device',
        title: 'Device',
        ellipsis: true,
        render: (_: unknown, record: UserRecord) =>
          record.login_logs.length > 0 ? (
            <Typography.Text>{record.login_logs[0].device}</Typography.Text>
          ) : (
            <CloseOutlined style={{ color: 'red' }} />
          ),
      },
      {
        dataIndex: 'login_logs',
        key: 'appVersion',
        title: 'App Version',
        render: (_: unknown, record: UserRecord) =>
          record.login_logs.length > 0 ? (
            <Typography.Text>{record.login_logs[0].appVersion}</Typography.Text>
          ) : (
            <CloseOutlined style={{ color: 'red' }} />
          ),
      },
      {
        dataIndex: 'actions',
        key: 'actions',
        title: 'Actions',
        render: (_: unknown, record: UserRecord) => {
          const targetRoles = (record.roles as string[]) || [];
          const hasAnyPermission =
            canEditUser(targetRoles) ||
            canEditDepartments ||
            (canEditRoles(targetRoles) && assignableRoles.length > 0) ||
            canBanUser(currentUserRoles, targetRoles);

          if (!hasAnyPermission) return null;

          return (
            <Tooltip title="Edit user">
              <Button type="text" icon={<EditOutlined />} onClick={() => setEditingUser(record)} />
            </Tooltip>
          );
        },
      },
    ],
    [canEditUser, canEditDepartments, canEditRoles, assignableRoles, currentUserRoles]
  );

  const handlePageChange = useCallback(
    (page: number, pageSize?: number) => {
      setPagination({ current: page, pageSize: pageSize || pagination.pageSize });
    },
    [pagination.pageSize]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(400);
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setTableHeight(containerRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const usersDataSource = usersData?.user || [];
  const tableSkeleton = useTableSkeleton(columns, usersDataSource, usersLoading, 20);

  return (
    <Layout
      style={{
        height: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}>
        <Space style={{ padding: 8 }}>
          <Button onClick={handleAddUser}>Add user</Button>
          <Switch
            className="app-switch"
            style={{ marginLeft: 10 }}
            onChange={setSwitchChecked}
            checkedChildren="Inactive users"
            unCheckedChildren="Active users"
          />
          <Search
            placeholder="Search by name or email"
            onSearch={onSearch}
            style={{ width: 300 }}
            allowClear
          />
        </Space>
        <div ref={containerRef} style={{ flex: 1 }}>
          <Table
            className="app-table"
            columns={tableSkeleton.columns}
            dataSource={tableSkeleton.dataSource}
            pagination={false}
            loading={tableSkeleton.loading}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ y: tableHeight - 56 }}
          />
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            zIndex: 1000,
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={usersData?.user_aggregate.aggregate?.count || 0}
            onChange={handlePageChange}
          />
        </div>
      </div>
      <NewUserModalView visible={modalVisible} onCancel={() => setModalVisible(false)} />
      <EditUserModalView
        visible={!!editingUser}
        user={editingUser}
        permissions={editingUserPermissions}
        assignableRoles={assignableRoles}
        loading={editLoading}
        banLoading={blockUserMutation.isPending}
        onCancel={() => setEditingUser(null)}
        onSubmit={handleEditSubmit}
        onBanUnban={handleBanFromModal}
      />
    </Layout>
  );
};
